import {
  AllWorkspacesDocument,
  CreateWorkspaceInput,
  RecentWorkspacesDocument,
  useCreateWorkspaceMutation,
} from "@gql/sites-api/graphql";
import { LandTechEndpoints, routedClient } from "react-migration/lib/persistence/apollo";
import { WorkspaceModalSource, logEventV2 } from "react-migration/lib/util/logEventV2";

import { ApolloError } from "@apollo/client";
import { Icon } from "react-migration/components/Icon";
import { WORKSPACE_TITLE_ALREADY_EXISTS_CODE } from "../WorkspaceModal/constants";
import { WorkspaceModal } from "../WorkspaceModal/WorkspaceModal";
import { setSelectedWorkspaceId } from "src/js/stores/site/actions/setSelectedWorkspaceId";
import { useCallback } from "react";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";

type NewWorkspaceModalProps = {
  source: WorkspaceModalSource;
  onClose: () => void;
};

export const NewWorkspaceModal = ({ source, onClose }: NewWorkspaceModalProps) => {
  const { t } = useTranslation();

  const [createWorkspace, { loading, error, reset }] = useCreateWorkspaceMutation({
    client: routedClient,
    context: {
      endpoint: LandTechEndpoints.Sites,
    },
    refetchQueries: [AllWorkspacesDocument, RecentWorkspacesDocument],
    onCompleted: ({ createWorkspace: { id } }) => {
      setSelectedWorkspaceId(id);
    },
  });

  const handleClose = useCallback(() => {
    logEventV2({ name: "New workspace - closed", properties: { source } });
    onClose();
  }, [onClose, source]);

  const handleCreateWorkspace = useCallback(
    async (input: CreateWorkspaceInput) => {
      try {
        const { errors } = await createWorkspace({
          variables: { input },
        });

        if (!errors?.length) {
          logEventV2({
            name: "Workspace created",
            properties: {
              workspaceName: input.title,
              developmentType: input.developmentType[0],
              descriptionPresent: Boolean(input.goal),
              descriptionText: input.goal || undefined,
              imageUploaded: Boolean(input.image),
              source,
            },
          });
          onClose();
        }
      } catch (error) {
        console.error("Failed to create workspace:", error);
      }
    },
    [createWorkspace, onClose, source]
  );

  const errorMessage = getTitleErrorMessage(error);
  const hasGenericError = !!error && !errorMessage;

  return (
    <WorkspaceModal
      title={<NewWorkspaceModalTitle />}
      actionLabel={t("workspaces.modal.create_workspace")}
      handleAction={handleCreateWorkspace}
      onClose={handleClose}
      loading={loading}
      titleError={errorMessage}
      genericError={hasGenericError}
      onErrorDismiss={reset}
    />
  );
};

const NewWorkspaceModalTitle = () => {
  const { t } = useTranslation();
  return (
    <span className="flex items-center text-lg font-semibold gap-x-2">
      <Icon icon="icon-lt-folder-add-line" size="lg" />
      <span>{t("workspaces.modal.new_workspace")}</span>
    </span>
  );
};

function getTitleErrorMessage(error?: ApolloError) {
  const titleError = error?.graphQLErrors?.find(
    (error) => error.extensions?.code === WORKSPACE_TITLE_ALREADY_EXISTS_CODE
  );
  return titleError?.message;
}
